const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000/api';

// Get base URL without /api suffix for storage URLs.
// Works both locally (API base = e.g. http://localhost:8000) and in production (API base = your cPanel API URL).
// In development, returns same-origin /api-storage/... so Next.js proxies the request and avoids 403 (e.g. Referer blocking) when frontend and API are on different origins.
export const getStorageUrl = (path: string | null | undefined): string | null => {
  if (!path || typeof path !== 'string') return null;
  const trimmed = path.trim();
  if (!trimmed) return null;
  // If backend already returns a full URL (e.g. production CDN or S3), use as-is
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  const normalizedPath = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  // In development, use same-origin proxy so image requests don't get 403 from backend (cross-origin Referer)
  if (process.env.NODE_ENV === 'development') {
    const pathWithoutStorage = normalizedPath.replace(/^\/storage\/?/, '');
    return `/api-storage/${pathWithoutStorage}`;
  }
  const baseUrl = API_URL.replace(/\/api\/?$/, '');
  return `${baseUrl}${normalizedPath}`;
};

interface ApiOptions extends RequestInit {
  token?: string;
}

export async function apiRequest<T>(
  endpoint: string,
  options: ApiOptions = {}
): Promise<T> {
  const { token, ...fetchOptions } = options;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(options.headers || {}),
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...fetchOptions,
    headers,
  });

  const data = await response.json();

  if (!response.ok) {
    const error = new Error(data.message || 'API request failed') as Error & {
      errors?: Record<string, string[]>;
      status?: number;
    };
    error.errors = data.errors;
    error.status = response.status;
    throw error;
  }

  return data;
}

export const api = {
  get: <T>(endpoint: string, token?: string) =>
    apiRequest<T>(endpoint, { method: 'GET', token }),

  post: <T>(endpoint: string, body: unknown, token?: string) =>
    apiRequest<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
      token,
    }),

  put: <T>(endpoint: string, body: unknown, token?: string) =>
    apiRequest<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
      token,
    }),

  delete: <T>(endpoint: string, token?: string) =>
    apiRequest<T>(endpoint, { method: 'DELETE', token }),

  // Upload files with FormData (no Content-Type header - browser sets it with boundary)
  upload: async <T>(endpoint: string, formData: FormData, token?: string): Promise<T> => {
    const headers: HeadersInit = {
      Accept: 'application/json',
    };

    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers,
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.message || 'Upload failed') as Error & {
        errors?: Record<string, string[]>;
        status?: number;
      };
      error.errors = data.errors;
      error.status = response.status;
      throw error;
    }

    return data;
  },
};
